import { useEffect, useRef } from 'react';
import { t } from '../../i18n/i18n';
import './CodeInput.scss';

/**
 * The one-time-code field: one box per digit rather than a single input.
 *
 * The digits arrive one at a time off a phone screen, and a box each shows how
 * far through the code you are without counting characters. Paste still works —
 * a pasted string fills the row and lands the caret on the first empty box.
 *
 * Lives here rather than beside a page because both ends of 2FA need it: the
 * Profile section that arms it, and the sign-in challenge that answers it.
 *
 * `digits` is a string array of `length` entries; `onChange` gets the next one.
 */
export default function CodeInput({
  digits,
  onChange,
  length = 6,
  invalid,
  disabled,
  label,
  autoFocus,
}) {
  const boxes = useRef([]);

  const focus = (i) => boxes.current[Math.min(Math.max(i, 0), length - 1)]?.focus();

  // Done from an effect rather than the `autoFocus` attribute: a dialog that
  // opens with the caret already in the first box is the point, and React's
  // attribute fires before the dialog has finished taking focus itself.
  // Re-runs once a rejected code is cleared and the boxes re-enable, so the
  // retry starts in the first box instead of needing a click.
  const empty = digits.every((d) => !d);
  useEffect(() => {
    if (autoFocus && empty && !disabled) boxes.current[0]?.focus();
  }, [autoFocus, empty, disabled]);

  const write = (i, value) => {
    const next = [...digits];
    next[i] = value;
    onChange(next);
  };

  const onKeyDown = (event, i) => {
    if (event.key === 'Backspace' && !digits[i]) {
      // Nothing to clear here, so step back and clear that one instead.
      event.preventDefault();
      write(i - 1, '');
      focus(i - 1);
    }
    if (event.key === 'ArrowLeft') focus(i - 1);
    if (event.key === 'ArrowRight') focus(i + 1);
  };

  const onPaste = (event, i) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    event.preventDefault();
    const next = [...digits];
    pasted.split('').slice(0, length - i).forEach((char, offset) => {
      next[i + offset] = char;
    });
    onChange(next);
    const firstEmpty = next.findIndex((d) => !d);
    focus(firstEmpty === -1 ? length - 1 : firstEmpty);
  };

  return (
    <div className={`code-input${invalid ? ' is-invalid' : ''}`} role="group" aria-label={label != null ? t(label) : t('{{length}}-digit code', { length })}>
      {digits.map((digit, i) => (
        <input
          // Position is the identity here — fixed boxes that never reorder.
          key={`digit-${i}`}
          ref={(el) => {
            boxes.current[i] = el;
          }}
          className={`code-input__box${digit ? ' is-filled' : ''}`}
          value={digit}
          inputMode="numeric"
          disabled={disabled}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={t('Digit {{n}}', { n: i + 1 })}
          onChange={(event) => {
            const char = event.target.value.replace(/\D/g, '').slice(-1);
            write(i, char);
            if (char) focus(i + 1);
          }}
          onKeyDown={(event) => onKeyDown(event, i)}
          onPaste={(event) => onPaste(event, i)}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </div>
  );
}

/** The empty value for a `length`-digit code — saves every caller writing it. */
export const emptyCode = (length = 6) => Array(length).fill('');
